export interface Renderer {
  device: GPUDevice
  context: GPUCanvasContext
  format: GPUTextureFormat
  canvas: HTMLCanvasElement
  width: number
  height: number
  pixelRatio: number
  onResize(cb: (w: number, h: number) => void): void
}

export async function createRenderer(canvas: HTMLCanvasElement): Promise<Renderer> {
  if (!navigator.gpu) throw new Error('WebGPU not available')
  const adapter = await navigator.gpu.requestAdapter()
  if (!adapter) throw new Error('No GPU adapter')
  const device = await adapter.requestDevice()
  const context = canvas.getContext('webgpu')
  if (!context) throw new Error('Failed to get webgpu context')
  const format = navigator.gpu.getPreferredCanvasFormat()

  const listeners = new Set<(w: number, h: number) => void>()
  const state = { width: 0, height: 0, pixelRatio: Math.min(window.devicePixelRatio, 2) }

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio, 2)
    const w = Math.max(1, Math.floor(canvas.clientWidth * dpr))
    const h = Math.max(1, Math.floor(canvas.clientHeight * dpr))
    if (w === state.width && h === state.height && dpr === state.pixelRatio) return
    state.width = w
    state.height = h
    state.pixelRatio = dpr
    canvas.width = w
    canvas.height = h
    context.configure({
      device,
      format,
      alphaMode: 'opaque',
    })
    listeners.forEach(cb => cb(w, h))
  }

  resize()
  window.addEventListener('resize', resize)

  return {
    device,
    context,
    format,
    canvas,
    get width() {
      return state.width
    },
    get height() {
      return state.height
    },
    get pixelRatio() {
      return state.pixelRatio
    },
    onResize(cb) {
      listeners.add(cb)
    },
  }
}
